import { formatGr, type Id } from '@odhaczka/core';
import { quantityLabel } from '../../data/lists';
import { articleName } from '../../data/prices';
import type { ArticleRow } from '../../db/types';
import type { DraftItem, Resolution } from '../../data/products';
import { cardCls, sectionTitleCls } from '../ui/classes';
import { asksAbout, type Draft } from './useDraft';

/** Podgląd rozpoznanych pozycji: ilość, uwaga, „znany/nowy” i pytania o podobne produkty. */
export function DraftPreview({ draft, title = 'Pozycje' }: { draft: Draft; title?: string }) {
  if (draft.items.length === 0) return null;
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline gap-2">
        <h2 className={`${sectionTitleCls} flex-1`}>
          {title} ({draft.items.length})
        </h2>
        {draft.aiPending && <span className="px-1 text-xs text-stone-500 motion-safe:animate-pulse">sprawdzam podobne…</span>}
      </div>
      <ul className={`${cardCls} divide-y divide-stone-200 dark:divide-stone-800`}>
        {draft.items.map((item, i) => (
          <PreviewRow
            key={`${i}:${item.key}`}
            item={item}
            answer={draft.answerFor(item)}
            onAnswer={(a) => draft.answer(item.key, a)}
            articles={draft.articlesFor(item)}
            pinned={draft.pinnedFor(item)}
            onPin={(id) => draft.pin(item.key, id)}
          />
        ))}
      </ul>
      {draft.unanswered > 0 && <p className="px-1 text-xs text-stone-500">Pozycje bez odpowiedzi trafią na listę jako nowe produkty.</p>}
    </section>
  );
}

interface RowProps {
  item: DraftItem;
  answer: Resolution | undefined;
  onAnswer: (a: Resolution | null) => void;
  /** Konkretne towary z paragonów do przypięcia (drugi poziom podpowiedzi). */
  articles?: ArticleRow[];
  pinned?: Id | undefined;
  onPin?: (articleId: Id | null) => void;
}

/** Jedna rozpoznana pozycja z pytaniem „to samo?” — także w edycji pozycji. */
export function PreviewRow({ item, answer, onAnswer, articles = [], pinned, onPin }: RowProps) {
  const { line } = item;
  const qty = quantityLabel({
    qty: line.qty,
    unit: line.unit,
    amountValue: line.amount?.value ?? null,
    amountUnit: line.amount?.unit ?? null,
  });
  const ask = asksAbout(item);
  const single = item.candidates.length === 1;
  const sameName = answer?.kind === 'same' ? item.candidates.find((c) => c.productId === answer.productId)?.name : undefined;

  return (
    <li className="px-4 py-3">
      <div className="flex items-baseline gap-2">
        <span className="min-w-0 flex-1 truncate font-medium">{line.name}</span>
        {qty && <span className="shrink-0 text-sm tabular-nums text-stone-500">{qty}</span>}
        {item.exact ? (
          <Badge className="bg-brand-100 text-brand-800 dark:bg-brand-800/30 dark:text-brand-100">znany</Badge>
        ) : !ask ? (
          <Badge className="bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300">nowy</Badge>
        ) : null}
      </div>
      {line.note && <p className="mt-0.5 text-sm text-stone-500 italic">{line.note}</p>}
      {onPin && articles.length > 0 && (
        <div className="-mx-1 mt-1.5 flex gap-1.5 overflow-x-auto px-1 pb-0.5">
          {articles.map((a) => {
            const on = pinned === a.id;
            return (
              <button
                key={a.id}
                type="button"
                aria-pressed={on}
                onClick={() => onPin(on ? null : a.id)}
                className={`min-h-9 max-w-[75%] shrink-0 truncate rounded-full px-3 text-sm ${
                  on ? 'bg-brand-700 font-medium text-white' : 'border border-stone-300 text-stone-700 dark:border-stone-700 dark:text-stone-300'
                }`}
              >
                {on && '✓ '}
                {articleName(a)}
                {a.lastShelfGr !== null && <span className={on ? '' : 'text-stone-500'}> · {formatGr(a.lastShelfGr)}{a.weight ? '/kg' : ''}</span>}
              </button>
            );
          })}
        </div>
      )}

      {ask && answer && (
        <div className="mt-1 flex items-center gap-2 text-sm text-stone-500">
          <span className="min-w-0 flex-1">{sameName ? `✓ to samo co „${sameName}”` : 'nowy produkt'}</span>
          <button type="button" onClick={() => onAnswer(null)} className="min-h-11 shrink-0 px-2 font-medium text-brand-700 dark:text-brand-500">
            zmień
          </button>
        </div>
      )}

      {ask && !answer && (
        <div className="mt-2 flex flex-col gap-3 rounded-xl bg-amber-50 p-3 dark:bg-amber-900/20">
          {item.candidates.map((c) => (
            <div key={c.productId} className="flex flex-col gap-2">
              <p className="text-sm">
                {c.source === 'ai' && (
                  <span
                    title="Podpowiedź AI"
                    className="mr-1.5 inline-block rounded-full bg-violet-100 px-1.5 py-0.5 align-middle text-xs font-semibold text-violet-800 dark:bg-violet-900/50 dark:text-violet-200"
                  >
                    AI
                  </span>
                )}
                Czy „{line.name}” to to samo co „{c.name}”?
              </p>
              <div className="grid grid-cols-2 gap-2">
                <AnswerButton primary onClick={() => onAnswer({ kind: 'same', productId: c.productId })}>
                  Tak, to samo
                </AnswerButton>
                {single && <AnswerButton onClick={() => onAnswer({ kind: 'new' })}>Nie, inny produkt</AnswerButton>}
              </div>
            </div>
          ))}
          {!single && <AnswerButton onClick={() => onAnswer({ kind: 'new' })}>Nie, inny produkt</AnswerButton>}
        </div>
      )}
    </li>
  );
}

function Badge({ className, children }: { className: string; children: string }) {
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>{children}</span>;
}

function AnswerButton({ primary = false, onClick, children }: { primary?: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-11 rounded-lg px-3 text-sm font-medium ${
        primary
          ? 'bg-brand-700 text-white active:bg-brand-800'
          : 'border border-stone-300 bg-white active:bg-stone-100 dark:border-stone-700 dark:bg-stone-900 dark:active:bg-stone-800'
      }`}
    >
      {children}
    </button>
  );
}
